import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  Check, Loader2, Sparkles, Building2, Rocket, 
  ArrowRight, Shield, Clock, Users, Zap, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/UserAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Plan data — 4-tier OPERATR pricing (Personal / Pro / Business / Enterprise)
const PLANS = {
  personal: {
    name: 'Personal OS',
    description: 'For individuals running their personal life',
    icon: Sparkles,
    color: 'purple',
    features: [
      'Unified Personal Dashboard',
      'Email Management (5 accounts)',
      'Calendar Sync',
      'Task & Project Management',
      'Basic AI Assistant',
      'Document Storage (25GB)',
      'Mobile Access',
      'Community Support'
    ],
    free: {
      id: 'personal_free_trial',
      days: 14,
      label: '14-day free trial'
    },
    monthly: {
      id: 'personal_monthly',
      setup: 500,
      price: 149,
      total: 649,
      label: '$149/mo'
    },
    annual: {
      id: 'personal_annual',
      setup: 500,
      price: 1788,    // 149 * 12
      total: 2288,
      label: '$1,788/yr'
    }
  },
  pro: {
    name: 'Pro OS',
    description: 'For founders and small teams running everything from one place',
    icon: Rocket,
    color: 'blue',
    popular: true,
    users: { included: 15, perExtra: 60 },
    features: [
      'Everything in Personal OS, plus:',
      '5 Team Users Included',
      'Unlimited Email Accounts',
      'CRM & Client Management',
      'Team Collaboration',
      'Project Management',
      'AI Workflow Automations',
      'Custom Dashboards',
      'Advanced Analytics & Reporting',
      'API & Third-Party Integrations',
      'Priority Support'
    ],
    free: {
      id: 'pro_free_trial',
      days: 14,
      label: '14-day free trial'
    },
    monthly: {
      id: 'pro_monthly',
      setup: 8000,
      price: 1200,
      total: 9200,
      label: '$1,200/mo'
    },
    annual: {
      id: 'pro_annual',
      setup: 8000,
      price: 14400,    // 1200 * 12
      total: 22400,
      label: '$14,400/yr'
    }
  },
  business: {
    name: 'Business OS',
    description: 'For growing businesses needing scale and customization',
    icon: Building2,
    color: 'emerald',
    users: { included: 50, perExtra: 50 },
    features: [
      'Everything in Pro OS, plus:',
      '50 Team Users Included',
      'Multi-Department Workflows',
      'Advanced Inventory & Supply Chain',
      'Full Client & Vendor Portals',
      'Custom Branded Dashboards',
      'Dedicated Account Manager',
      'White-Glove Onboarding',
      'Monthly Strategy Calls',
      'SOC 2-ready Security Controls',
      '1TB Storage'
    ],
    monthly: {
      id: 'business_monthly',
      setup: 35000,
      price: 5000,
      total: 40000,
      label: '$5,000/mo'
    },
    annual: {
      id: 'business_annual',
      setup: 35000,
      price: 60000,    // 5000 * 12
      total: 95000,
      label: '$60,000/yr'
    }
  },
  enterprise: {
    name: 'Enterprise OS',
    description: 'For organizations needing white-label, SLA, and custom builds',
    icon: Shield,
    color: 'amber',
    users: { included: 250, perExtra: 40 },
    features: [
      'Everything in Business OS, plus:',
      '250 Team Users Included',
      'Unlimited Everything',
      'White-Label & Custom Domains',
      'On-premise Deployment Option',
      'Custom Development & Integrations',
      'Dedicated CSM + Strategy Pod',
      'SLA Guarantees',
      '24/7 Phone Support',
      'Advanced Security & Compliance',
      'Custom Training Programs',
      'Unlimited Storage'
    ],
    monthly: {
      id: 'enterprise_monthly',
      setup: 80000,
      price: 10000,
      total: 90000,
      label: '$10,000/mo'
    },
    annual: {
      id: 'enterprise_annual',
      setup: 80000,
      price: 120000,   // 10000 * 12
      total: 200000,
      label: '$120,000/yr'
    }
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const PricingCard = ({ plan, planKey, billing, onSelect, loading, selectedPlan }) => {
  const isSelected = selectedPlan === plan[billing].id;
  const Icon = plan.icon;
  const colorMap = {
    purple: {
      bg: 'from-purple-500/20 to-purple-600/10',
      border: 'border-purple-500/50',
      text: 'text-purple-400',
      button: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
    },
    blue: {
      bg: 'from-blue-500/20 to-blue-600/10',
      border: 'border-blue-500/50',
      text: 'text-blue-400',
      button: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
    },
    emerald: {
      bg: 'from-emerald-500/20 to-emerald-600/10',
      border: 'border-emerald-500/50',
      text: 'text-emerald-400',
      button: 'from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'
    },
    amber: {
      bg: 'from-amber-500/20 to-amber-600/10',
      border: 'border-amber-500/50',
      text: 'text-amber-400',
      button: 'from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
    }
  };
  const colors = colorMap[plan.color];

  return (
    <div 
      className={`relative bg-gradient-to-b ${colors.bg} border ${plan.popular ? 'border-blue-500' : 'border-gray-700'} rounded-2xl p-6 flex flex-col transition-all ${
        isSelected ? 'ring-2 ring-white scale-[1.02]' : 'hover:border-gray-500'
      }`}
    >
      {/* Popular Badge */}
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-blue-500 text-white text-xs font-bold px-4 py-1 rounded-full">
            MOST POPULAR
          </span>
        </div>
      )}

      {/* Icon & Name */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-3 rounded-xl bg-${plan.color}-500/20`}>
          <Icon size={24} className={colors.text} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{plan.name}</h3>
          <p className="text-gray-400 text-sm">{plan.description}</p>
        </div>
      </div>

      {/* Pricing */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold text-white">
            {formatCurrency(plan[billing].price)}
          </span>
          <span className="text-gray-400">
            /{billing === 'monthly' ? 'month' : 'year'}
          </span>
        </div>
        <p className="text-gray-500 text-sm mt-1">
          + {formatCurrency(plan[billing].setup)} setup fee (one-time)
        </p>
        {billing === 'annual' && plan[billing].savings && (
          <p className="text-green-400 text-sm mt-1">
            Save {formatCurrency(plan[billing].savings)}/year vs monthly
          </p>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-6 flex-1">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <Check size={18} className={`${colors.text} flex-shrink-0 mt-0.5`} />
            <span className="text-gray-300 text-sm">{feature}</span>
          </li>
        ))}
      </ul>

      {/* User seats callout for team plans */}
      {plan.users && (
        <div className={`mb-5 flex items-center gap-3 px-4 py-3 rounded-xl border ${colors.border}`}>
          <Users size={18} className={colors.text} />
          <div>
            <p className={`text-sm font-semibold ${colors.text}`}>{plan.users.included} users included</p>
            <p className="text-gray-400 text-xs">+${plan.users.perExtra}/user/month after {plan.users.included}</p>
          </div>
        </div>
      )}

      {/* Free Trial Button */}
      {plan.free && (
        <button
          onClick={() => onSelect(plan.free.id)}
          disabled={loading}
          className="w-full py-3 mb-3 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 text-green-400 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid={`select-plan-${planKey}-free`}
        >
          {loading && selectedPlan === plan.free.id ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Activating...
            </>
          ) : (
            <>
              <Gift size={18} />
              Start Free Trial
            </>
          )}
        </button>
      )}

      {/* CTA Button */}
      <button
        onClick={() => onSelect(plan[billing].id)}
        disabled={loading}
        className={`w-full py-3 bg-gradient-to-r ${colors.button} text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50`}
        data-testid={`select-plan-${planKey}-${billing}`}
      >
        {loading && isSelected ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Processing...
          </>
        ) : (
          <>
            Get Started
            <ArrowRight size={18} />
          </>
        )}
      </button>

      {/* Total */}
      <p className="text-center text-gray-500 text-xs mt-3">
        Total today: {formatCurrency(plan[billing].total)}
      </p>
    </div>
  );
};

const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, token, isAuthenticated, loading: authLoading, refreshUserData } = useAuth();
  
  const [billing, setBilling] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Check if returning from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const planId = searchParams.get('plan_id');
    
    if (sessionId) {
      checkPaymentStatus(sessionId, planId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, planId) => {
    setCheckingStatus(true);
    try {
      const response = await fetch(`${API_URL}/api/pricing/status/${sessionId}`);
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Welcome to OPERATR!');
        
        // Refresh user data to get updated subscription status
        await refreshUserData();
        
        // Redirect to onboarding
        setTimeout(() => {
          navigate('/onboarding');
        }, 1500);
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
      } else {
        toast.info('Payment is being processed...');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Failed to verify payment status');
    } finally {
      setCheckingStatus(false);
      // Clear URL params
      window.history.replaceState({}, document.title, '/pricing');
    }
  };

  const handleFreePlan = async () => {
    if (!isAuthenticated) {
      toast.info('Please sign in first to activate the free plan');
      navigate('/auth', { state: { from: location, returnToPricing: true } });
      return;
    }
    setLoading(true);
    setSelectedPlan('free');
    try {
      const res = await fetch(`${API_URL}/api/pricing/activate-free`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user?.user_id })
      });
      const data = await res.json();
      if (data.success) {
        // Update localStorage so auth context reflects the change
        if (data.user) {
          localStorage.setItem('auth_user', JSON.stringify(data.user));
        }
        toast.success('Free plan activated! Setting up your workspace...');
        setTimeout(() => navigate('/onboarding'), 1000);
      } else {
        throw new Error(data.detail || 'Activation failed');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to activate free plan');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleSelectPlan = async (planId) => {
    // If not authenticated, redirect to auth first
    if (!isAuthenticated) {
      toast.info('Please sign in to continue with your purchase');
      navigate('/auth', { state: { from: location, returnToPricing: true, selectedPlan: planId } });
      return;
    }
    
    setSelectedPlan(planId);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/pricing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
          metadata: {
            user_email: user?.email,
            user_id: user?.user_id
          }
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create checkout session');
      }
      
      // Handle FREE plans - redirect directly to success URL
      if (data.free) {
        toast.success('Free trial activated! Setting up your workspace...');
        // Refresh user data and redirect to onboarding
        await refreshUserData();
        setTimeout(() => navigate('/onboarding'), 500);
        return;
      }
      
      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-white text-lg">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-12 px-4">
      <div className="max-w-[1400px] mx-auto px-2">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Get started with OPERATR - Your Personal Operating System for managing everything in one place.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              billing === 'monthly'
                ? 'bg-white text-black'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
            data-testid="billing-monthly"
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling('annual')}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              billing === 'annual'
                ? 'bg-white text-black'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
            data-testid="billing-annual"
          >
            Annual
            <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
              Save up to 15%
            </span>
          </button>
        </div>

        {/* Paid Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {Object.entries(PLANS).map(([key, plan]) => (
            <PricingCard
              key={key}
              planKey={key}
              plan={plan}
              billing={billing}
              onSelect={handleSelectPlan}
              loading={loading}
              selectedPlan={selectedPlan}
            />
          ))}
        </div>

        {/* Trust Badges */}
        <div className="border-t border-gray-800 pt-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Shield size={24} className="text-green-400" />
              </div>
              <h4 className="text-white font-medium mb-1">Secure Payments</h4>
              <p className="text-gray-500 text-sm">Powered by Stripe</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Clock size={24} className="text-blue-400" />
              </div>
              <h4 className="text-white font-medium mb-1">24/7 Support</h4>
              <p className="text-gray-500 text-sm">We're here to help</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Users size={24} className="text-purple-400" />
              </div>
              <h4 className="text-white font-medium mb-1">Trusted by Teams</h4>
              <p className="text-gray-500 text-sm">500+ businesses</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Zap size={24} className="text-amber-400" />
              </div>
              <h4 className="text-white font-medium mb-1">Instant Setup</h4>
              <p className="text-gray-500 text-sm">Get started in minutes</p>
            </div>
          </div>
        </div>

        {/* FAQ or Contact */}
        <div className="text-center mt-12">
          <p className="text-gray-400">
            Have questions?{' '}
            <a href="mailto:creatrhq@gmail.com" className="text-purple-400 hover:text-purple-300 underline">
              Contact our team
            </a>
          </p>
          
          {/* Auth Options */}
          <div className="mt-4 flex flex-col items-center gap-2">
            {isAuthenticated ? (
              <>
                <p className="text-gray-500 text-sm">
                  Signed in as <span className="text-gray-400">{user?.email}</span>
                </p>
                <button
                  onClick={() => {
                    // Clear auth and redirect to sign in
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('auth_user');
                    window.location.href = '/auth';
                  }}
                  className="text-purple-400 hover:text-purple-300 text-sm underline"
                  data-testid="sign-out-btn"
                >
                  Sign out and use a different account
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/auth')}
                className="text-purple-400 hover:text-purple-300 text-sm underline"
                data-testid="sign-in-link"
              >
                Already have an account? Sign in
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
